'use client';

import { RefObject, useEffect, useMemo, useState } from 'react';
import { useMotionValue, useReducedMotion, useSpring } from 'framer-motion';

type Offset =
  | { x: number; y: number }
  | ((args: { width?: number; height?: number }) => { x: number; y: number });

export type UseCursorFollowerOptions = {
  /** desliga automaticamente em telas touch */
  disableOnCoarse?: boolean;
  /** centraliza pelo tamanho do seguidor (ex.: 72 => -36, -36) */
  centerBySize?: number;
  /** offset custom */
  offset?: Offset;
  /** stiffness/damping/mass dos springs */
  spring?: { stiffness?: number; damping?: number; mass?: number };
  /** liga/desliga manualmente */
  enabled?: boolean;
};

export function useCursorFollower(
  containerRef: RefObject<HTMLElement>,
  {
    disableOnCoarse = true,
    centerBySize,
    offset,
    spring = { stiffness: 220, damping: 24, mass: 0.6 },
    enabled = true,
  }: UseCursorFollowerOptions = {}
) {
  const prefersReduced = useReducedMotion();
  const [visible, setVisible] = useState(false);

  const targetX = useMotionValue(0);
  const targetY = useMotionValue(0);
  const x = useSpring(targetX, spring);
  const y = useSpring(targetY, spring);

  const computedOffset = useMemo<Offset>(() => {
    if (typeof offset === 'function' || typeof offset === 'object')
      return offset;
    if (typeof centerBySize === 'number') {
      const half = centerBySize / 2;
      return { x: -half, y: -half };
    }
    return { x: 0, y: 0 };
  }, [offset, centerBySize]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (!enabled || prefersReduced) {
      setVisible(false);
      return;
    }

    // evita em telas touch
    if (disableOnCoarse && window.matchMedia('(pointer: coarse)').matches) {
      setVisible(false);
      return;
    }

    // garante posicionamento relativo
    if (getComputedStyle(el).position === 'static') {
      el.style.position = 'relative';
    }

    let rafId = 0;
    let inside = false;

    const last = { x: 0, y: 0, has: false };

    const getOffset = (w?: number, h?: number) => {
      if (typeof computedOffset === 'function')
        return computedOffset({ width: w, height: h });
      return computedOffset;
    };

    const computeAndSet = (xClient: number, yClient: number) => {
      const rect = el.getBoundingClientRect();
      const inX = xClient >= rect.left && xClient <= rect.right;
      const inY = yClient >= rect.top && yClient <= rect.bottom;
      const nowInside = inX && inY;

      if (nowInside) {
        const off = getOffset(undefined, undefined);
        targetX.set(xClient - rect.left + (off?.x ?? 0));
        targetY.set(yClient - rect.top + (off?.y ?? 0));
        if (!inside) setVisible(true);
      } else {
        if (inside) setVisible(false);
      }
      inside = nowInside;
    };

    const onPointerMove = (e: PointerEvent | MouseEvent) => {
      last.x = e.clientX;
      last.y = e.clientY;
      last.has = true;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => computeAndSet(last.x, last.y));
    };

    const onPointerEnter = (e: PointerEvent) => {
      last.x = e.clientX;
      last.y = e.clientY;
      last.has = true;
      computeAndSet(last.x, last.y);
    };

    const onPointerLeave = () => {
      inside = false;
      setVisible(false);
    };

    const onScrollOrResize = () => {
      if (!last.has) return;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => computeAndSet(last.x, last.y));
    };

    el.addEventListener('pointerenter', onPointerEnter, { passive: true });
    el.addEventListener('pointerleave', onPointerLeave, { passive: true });
    document.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });

    return () => {
      el.removeEventListener('pointerenter', onPointerEnter);
      el.removeEventListener('pointerleave', onPointerLeave);
      document.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [
    containerRef,
    computedOffset,
    disableOnCoarse,
    enabled,
    prefersReduced,
    targetX,
    targetY,
  ]);

  return { x, y, visible, setVisible, prefersReduced };
}
